import type { ZodTypeAny } from "zod";

import type { UserRole } from "./access-control";
import type { AppRouteKey } from "./app-metadata";
import { contactLastOrderDatesSchema } from "../contacts/contact-last-order-date";
import { DASHBOARD_AREA_KEYS, dashboardEndpointKey, type DashboardAreaKey, type DashboardEndpointKey } from "../dashboard/dashboard-areas";
import { dashboardAreaResponseSchema } from "../dashboard/dashboard-data";
import { DASHBOARD_ENVIRONMENTS, type AppEnvironment } from "./feature-flags";

export const API_PATH_PREFIX = "/api";

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export type ApiAccess = "public" | "session" | "tenant";

export type ApiEnvironments = "all" | readonly AppEnvironment[];

type SessionEndpoint = {
  access: "public" | "session";
};

type TenantEndpoint = {
  access: "tenant";
  methodOwnerPages?: Partial<Record<HttpMethod, AppRouteKey>>;
} & ({ ownerPage: AppRouteKey; roles?: never } | { roles: "all" | readonly UserRole[]; ownerPage?: never });

export type ApiEndpointMeta = {
  path: `${typeof API_PATH_PREFIX}/${string}`;
  environments: ApiEnvironments;
  response?: ZodTypeAny;
} & (SessionEndpoint | TenantEndpoint);

function dashboardEndpoint<K extends DashboardAreaKey>(area: K) {
  return {
    path: `${API_PATH_PREFIX}/dashboard/${area}`,
    access: "tenant",
    ownerPage: area,
    environments: DASHBOARD_ENVIRONMENTS,
    response: dashboardAreaResponseSchema,
  } as const;
}

function dashboardEndpointEntry(area: DashboardAreaKey) {
  return [dashboardEndpointKey(area), dashboardEndpoint(area)] as const;
}

type DashboardEndpoints = { [K in DashboardAreaKey as DashboardEndpointKey<K>]: ReturnType<typeof dashboardEndpoint<K>> };

const DASHBOARD_ENDPOINTS = Object.fromEntries(DASHBOARD_AREA_KEYS.map(dashboardEndpointEntry)) as DashboardEndpoints;

export const API_ENDPOINTS = {
  login: { path: "/api/login", access: "public", environments: "all" },
  logout: { path: "/api/logout", access: "public", environments: "all" },
  user: { path: "/api/user", access: "session", environments: "all" },
  register: { path: "/api/register", access: "session", environments: "all" },
  users: { path: "/api/users", access: "tenant", roles: "all", environments: "all" },
  chemicals: { path: "/api/chemicals", access: "tenant", ownerPage: "inventory", environments: "all" },
  inventoryTransactions: {
    path: "/api/inventory-transactions",
    access: "tenant",
    ownerPage: "transactions",
    methodOwnerPages: { POST: "inventory" },
    environments: "all",
  },
  orders: { path: "/api/orders", access: "tenant", ownerPage: "orders", environments: "all" },
  contacts: { path: "/api/contacts", access: "tenant", ownerPage: "contacts", environments: "all" },
  contactPeople: { path: "/api/contact-people", access: "tenant", ownerPage: "contacts", environments: "all" },
  addresses: { path: "/api/addresses", access: "tenant", ownerPage: "contacts", environments: "all" },
  contactLastOrderDates: {
    path: "/api/contact-last-order-dates",
    access: "tenant",
    ownerPage: "contacts",
    environments: "all",
    response: contactLastOrderDatesSchema,
  },
  suppliers: { path: "/api/suppliers", access: "tenant", ownerPage: "audit", environments: "all" },
  purchasers: { path: "/api/purchasers", access: "tenant", ownerPage: "audit", environments: "all" },
  audit: { path: "/api/audit-history", access: "tenant", ownerPage: "audit", environments: "all" },
  documentGeneration: { path: "/api/document-generation", access: "tenant", roles: "all", environments: "all" },
  ...DASHBOARD_ENDPOINTS,
} as const satisfies Record<string, ApiEndpointMeta>;

export type ApiEndpoints = typeof API_ENDPOINTS;
export type ApiEndpointKey = keyof ApiEndpoints;

export const API_ENDPOINT_KEYS = Object.keys(API_ENDPOINTS) as ApiEndpointKey[];

export const API_PATHS = Object.fromEntries(
  API_ENDPOINT_KEYS.map((key) => [key, API_ENDPOINTS[key].path]),
) as { [K in ApiEndpointKey]: ApiEndpoints[K]["path"] };

function ownsPath(entryPath: string, candidate: string): boolean {
  return candidate === entryPath || candidate.startsWith(`${entryPath}/`);
}

export function findApiEndpoint(path: string): ApiEndpointMeta | undefined {
  return API_ENDPOINT_KEYS
    .map((key): ApiEndpointMeta => API_ENDPOINTS[key])
    .find((entry) => ownsPath(entry.path, path));
}

export function isEndpointEnabledIn(entry: ApiEndpointMeta, environment: AppEnvironment): boolean {
  return entry.environments === "all" || entry.environments.includes(environment);
}
